/**
 * ============================================================================
 * UnivoCarousel
 * ============================================================================
 *
 * Purpose: One memoized carousel API for the project (like UnivoImage).
 * Responsibility: Apply presets/settings; render shared CustomCarousel engine.
 * Performance Notes:
 *   - React.memo + useMemo(config) avoid unnecessary re-renders (INP)
 *   - Uses lightweight native carousel (no Swiper CSS/JS on critical path)
 *   - Autoplay pauses on interaction via underlying engine
 * Risk Level: Low — opt-in migration per call site
 * ============================================================================
 */

"use client";

import {
  forwardRef,
  memo,
  useMemo,
  type ReactNode,
} from "react";
import NativeCarousel from "@/components/molecules/CustomCarousels/Carousel";
import { mergeCarouselSettings } from "./presets";
import type {
  UnivoCarouselHandle,
  UnivoCarouselPresetName,
  UnivoCarouselSettings,
} from "./types";

export type UnivoCarouselProps = {
  children: ReactNode[];
  /** Named homepage/shared preset. */
  preset?: UnivoCarouselPresetName;
  /** Overrides merged on top of preset. */
  settings?: UnivoCarouselSettings;
  className?: string;
  slidesContainerClassName?: string;
  slideWrapperClassName?: string;
  isSmoothBehaviour?: boolean;
  id?: string;
};

const UnivoCarouselBase = forwardRef<UnivoCarouselHandle, UnivoCarouselProps>(
  function UnivoCarousel(
    {
      children,
      preset,
      settings,
      className,
      slidesContainerClassName,
      slideWrapperClassName,
      isSmoothBehaviour,
      id,
    },
    ref
  ) {
    const config = useMemo(
      () => mergeCarouselSettings(preset, settings),
      [preset, settings]
    );

    return (
      <NativeCarousel
        ref={ref as any}
        config={config}
        className={className}
        slidesContainerClassName={slidesContainerClassName}
        slideWrapperClassName={slideWrapperClassName}
        isSmoothBehaviour={isSmoothBehaviour}
        id={id}
      >
        {children}
      </NativeCarousel>
    );
  }
);

const UnivoCarousel = memo(UnivoCarouselBase);
UnivoCarousel.displayName = "UnivoCarousel";

export default UnivoCarousel;
export { useUnivoCarousel } from "./useUnivoCarousel";
export { UNIVO_CAROUSEL_PRESETS, mergeCarouselSettings } from "./presets";
export type {
  UnivoCarouselHandle,
  UnivoCarouselPresetName,
  UnivoCarouselSettings,
  UnivoCarouselSlideChangeMeta,
} from "./types";
